import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { InlineSpinner } from "@/components/common/Loading";
import { Building2, Plus, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

export default function Organizations() {
  const { currentAccount } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/organizations", { params: { include_archived: true } }); setOrgs(data.organizations); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [currentAccount]);
  useEffect(() => { load(); }, [load]);

  // Order organizations hierarchically (parents followed by their children) with depth.
  const orderedOrgs = useMemo(() => {
    const byParent = {};
    orgs.forEach((o) => { const p = o.parent_org_id || "__root"; (byParent[p] = byParent[p] || []).push(o); });
    const out = [];
    const walk = (parentId, depth) => {
      (byParent[parentId] || []).forEach((o) => { out.push({ ...o, _depth: depth }); walk(o.id, depth + 1); });
    };
    walk("__root", 0);
    // include any orphans whose parent is filtered out
    orgs.forEach((o) => { if (!out.find((x) => x.id === o.id)) out.push({ ...o, _depth: 0 }); });
    return out;
  }, [orgs]);

  const columns = [
    { key: "name", header: "Organization", render: (r) => (
      <div className="flex items-center" style={{ paddingLeft: (r._depth || 0) * 22 }}>
        {r._depth > 0 && <span className="text-slate-300 mr-1.5 select-none">└</span>}
        <span className={`w-7 h-7 rounded-md flex items-center justify-center mr-2.5 ${r._depth > 0 ? "bg-slate-100" : "bg-brand/10"}`}>
          <Building2 className={`w-4 h-4 ${r._depth > 0 ? "text-slate-400" : "text-brand"}`} />
        </span>
        <span className="font-medium text-slate-800">{r.name}</span>
      </div>) },
    { key: "org_code", header: "Org Code", render: (r) => <span className="font-mono text-slate-600">{r.org_code}</span> },
    { key: "parent", header: "Parent", render: (r) => r.parent_org_name || <span className="text-slate-300">—</span> },
    { key: "members", header: "Members", render: (r) => r.member_count },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "actions", header: "", className: "text-right", render: (r) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`org-actions-${r.id}`} onClick={(e) => e.stopPropagation()} className="p-1 rounded hover:bg-slate-100"><MoreHorizontal className="w-4 h-4 text-slate-500" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white">
          <DropdownMenuItem onClick={() => { setEditing(r); setOpen(true); }} data-testid={`org-edit-${r.id}`}>Edit</DropdownMenuItem>
          <DropdownMenuItem className="text-rose-600" onClick={() => setDelTarget(r)} data-testid={`org-delete-${r.id}`}>Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>) },
  ];

  const del = async () => {
    try { await api.delete(`/organizations/${delTarget.id}`); toast.success("Organization deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiError(e)); setDelTarget(null); }
  };

  return (
    <div>
      <PageHeader title="Organizations" description="Flexible parent/child hierarchy with unique six-digit codes."
        actions={<Button data-testid="add-org-button" className="bg-[#006090] hover:bg-[#004E75]" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="w-4 h-4 mr-1.5" /> Add Organization</Button>} />
      <DataTable columns={columns} rows={orderedOrgs} loading={loading} testId="organizations-table"
        empty={<EmptyState icon={Building2} title="No organizations" description="Create your first organization to structure your account." />} />
      <OrgDialog open={open} onOpenChange={setOpen} editing={editing} orgs={orgs} onSaved={() => { setOpen(false); load(); }} />
      <ConfirmDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)} title="Delete organization?"
        description="It will be moved to the recycle bin. Organizations with child orgs cannot be deleted." destructive
        confirmLabel="Delete" onConfirm={del} />
    </div>
  );
}

function OrgDialog({ open, onOpenChange, editing, orgs, onSaved }) {
  const [form, setForm] = useState({ name: "", org_code: "", parent_org_id: "", description: "", status: "active" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (editing) setForm({ name: editing.name, org_code: editing.org_code, parent_org_id: editing.parent_org_id || "",
      description: editing.description || "", status: editing.status });
    else setForm({ name: "", org_code: "", parent_org_id: "", description: "", status: "active" });
    setError("");
  }, [editing, open]);

  const submit = async () => {
    if (!/^\d{6}$/.test(form.org_code)) return setError("Organization Code must be exactly six digits");
    setError(""); setLoading(true);
    try {
      const payload = { ...form, parent_org_id: form.parent_org_id || null };
      if (editing) await api.patch(`/organizations/${editing.id}`, payload);
      else await api.post("/organizations", payload);
      toast.success(editing ? "Organization updated" : "Organization created"); onSaved();
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="org-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit Organization" : "Add Organization"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {error && <p className="text-sm text-rose-600" data-testid="org-error">{error}</p>}
          <div>
            <Label className="text-xs font-semibold text-slate-700">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="org-name" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Organization Code (6 digits)</Label>
            <Input value={form.org_code} maxLength={6} inputMode="numeric"
              onChange={(e) => setForm({ ...form, org_code: e.target.value.replace(/\D/g, "") })} className="mt-1 font-mono" data-testid="org-code" placeholder="100100" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Parent organization</Label>
            <Select value={form.parent_org_id} onValueChange={(v) => setForm({ ...form, parent_org_id: v === "none" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="org-parent"><SelectValue placeholder="None (top-level)" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">None (top-level)</SelectItem>
                {orgs.filter((o) => !editing || o.id !== editing.id).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="org-submit" className="bg-[#006090] hover:bg-[#004E75]">{loading ? <InlineSpinner /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
